import { useState, useEffect, useCallback, useRef } from 'react';
import { useApolloClient } from '@apollo/react-hooks';

import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import { useMemoArray } from 'hooks/use-memo-array';

import {
  PersonalizedProductsModelNames,
  ProductMenuTypes,
  ProductsProvider,
  useGetPersonalizedProductsV2Query,
} from 'types/graphql';
import { parseProduct, Product } from 'utils/helpers/product';
import filteredProducts from 'shared/graphql/product/filtered-products.gql';

import { GetRecommendationsFallback, Options } from './use-product-recommendations';

export type UsePersonalizedItemsV2Return = {
  data?: Product[];
  error?: Error;
  loading: boolean;
};

export function usePersonalizedItemsV2(
  getRecommendationsFallback: GetRecommendationsFallback,
  productIds: string[],
  modelName: PersonalizedProductsModelNames,
  options: Options & { category?: string }
): UsePersonalizedItemsV2Return {
  const { limit = 10, skip = false, excludeProducts = [], category } = options;

  const apolloClient = useApolloClient();
  const { dispensary, loading: dispensaryLoading } = useDispensary();
  const { menuType } = useCart();
  const { isKiosk, variant: menuVariant } = useUI();

  const productIdsMemo = useMemoArray(productIds);
  const excludeProductsMemo = useMemoArray(excludeProducts);

  const [fallbackData, setFallbackData] = useState<Product[] | undefined>(undefined);
  const [fallbackError, setFallbackError] = useState<Error | undefined>(undefined);
  const [fallbackLoading, setFallbackLoading] = useState<boolean>(false);
  const [shouldUseFallback, setShouldUseFallback] = useState<boolean>(false);

  /**
   * IMPORTANT: Use refs to store callback functions to prevent useEffect re-execution loops.
   *
   * Without refs, these callbacks would be in the useEffect dependency array. During logout:
   * 1. apolloClient.clearStore() aborts in-flight queries with a NetworkError
   * 2. queryError changes, triggering the useEffect
   * 3. Simultaneously, dispensary/menuType change as user state clears
   * 4. This recreates getVariables (deps: [dispensary?.id, menuType, ...])
   * 5. The effect runs again because getVariables changed
   * 6. Steps 3-5 repeat rapidly as logout state propagates, freezing the browser
   *
   * By using refs, we keep the callbacks out of the dependency array. The effect only
   * runs when query state changes, not when callbacks are recreated. This matches the
   * pattern used in use-product-recommendations.ts.
   */
  const getRecommendationsFallbackRef = useRef(getRecommendationsFallback);
  getRecommendationsFallbackRef.current = getRecommendationsFallback;

  // Fallback product fetching logic (same as use-product-recommendations)
  const getProducts = useCallback(
    async (variables: any, productsProvider = ProductsProvider.dutchie) => {
      const excludeProductsSet = new Set(excludeProductsMemo);
      const filterProduct = ({ id }: Product): boolean => typeof id === 'string' && !excludeProductsSet.has(id);

      const { data } = await apolloClient.query({
        query: filteredProducts,
        variables,
      });

      return data.filteredProducts.products
        .map((product: any) => ({ ...parseProduct(product), provider: productsProvider }))
        .filter(filterProduct);
    },
    [apolloClient, excludeProductsMemo]
  );

  const getVariables = useCallback(
    (productsFilter: any = {}) => {
      if (!dispensary?.id) {
        throw new Error('missing dispensaryId variable');
      }

      return {
        productsFilter: {
          sortBy: 'inputOrder',
          bypassOnlineThresholds: isKiosk,
          dispensaryId: dispensary.id,
          isKioskMenu: isKiosk,
          pricingType: menuType,
          Status: 'Active' as const,
          removeProductsBelowOptionThresholds: true,
          ...productsFilter,
        },
        perPage: limit,
      };
    },
    [dispensary?.id, isKiosk, limit, menuType]
  );

  // Update refs for callbacks (see comment above for why refs are needed)
  const getProductsRef = useRef(getProducts);
  getProductsRef.current = getProducts;
  const getVariablesRef = useRef(getVariables);
  getVariablesRef.current = getVariables;

  // Filter out null/undefined values from productIds to match GraphQL schema requirements
  const filteredProductIds = productIdsMemo.filter((id): id is string => typeof id === 'string' && id.length > 0);

  // Determine if we should skip the query
  const shouldSkipQuery =
    skip ||
    dispensaryLoading ||
    !dispensary?.id ||
    !menuType ||
    !menuVariant ||
    (modelName !== PersonalizedProductsModelNames.recommendedForYou && filteredProductIds.length === 0);

  // Use the autogenerated GraphQL hook
  const { data: queryData, loading: queryLoading, error: queryError } = useGetPersonalizedProductsV2Query({
    variables: {
      input: {
        dispensaryId: dispensary?.id || '',
        limit,
        filters: {
          menuType: menuType || ProductMenuTypes.rec,
          menuVariant: menuVariant || 'default',
          ...(category && { category }),
        },
        modelName,
        productIds: filteredProductIds,
      },
    },
    skip: shouldSkipQuery,
    errorPolicy: 'all', // Allow partial data and handle errors manually
  });

  // Handle fallback logic when the main query fails or returns no data
  useEffect(() => {
    // Don't trigger fallback if the query was skipped
    if (shouldSkipQuery) {
      return;
    }

    const hasError = queryError;
    const hasNoData = queryData?.getPersonalizedProductsV2 && !queryData.getPersonalizedProductsV2.products.length;

    if ((hasError || hasNoData) && !shouldUseFallback && !fallbackLoading) {
      setShouldUseFallback(true);
      setFallbackLoading(true);

      // Use the same fallback strategy as use-product-recommendations
      // Access callbacks via refs to avoid dependency array issues (see comment above)
      try {
        getRecommendationsFallbackRef
          .current((productFilters) => getProductsRef.current(getVariablesRef.current(productFilters)))
          .then(setFallbackData)
          .catch(setFallbackError)
          .finally(() => setFallbackLoading(false));
      } catch (syncError) {
        // Catch synchronous errors (like from getVariables)
        setFallbackError(syncError as Error);
        setFallbackLoading(false);
      }
    }
    // Callbacks are accessed via refs, so they're intentionally excluded from the dependency array.
    // This prevents re-execution loops during logout when callbacks are recreated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryError, queryData, shouldUseFallback, fallbackLoading, shouldSkipQuery]);

  // Process and filter the successful query data
  const processedData = queryData?.getPersonalizedProductsV2?.products
    ? queryData.getPersonalizedProductsV2.products
        .map((product: any) => parseProduct(product))
        .filter((product: Product) => {
          const excludeProductsSet = new Set(excludeProductsMemo);
          return typeof product.id === 'string' && !excludeProductsSet.has(product.id);
        })
    : undefined;

  // Return the appropriate data based on whether we're using fallback or not
  if (shouldUseFallback) {
    return {
      data: fallbackData,
      loading: fallbackLoading,
      error: fallbackError,
    };
  }

  return {
    data: processedData,
    loading: queryLoading || dispensaryLoading,
    error: queryError ? new Error(queryError.message) : undefined,
  };
}
